import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AppThunk } from "./store";
import { getOrgId } from "./orgHelpers";
import { openToast } from "./ToastSlice";
import {
  ClientDepositGroup,
  CreateDepositAccountDto,
  DepositAccount,
  DepositAccountFormOptions,
} from "../models/DepositAccount";
import { DepositAccountService } from "../services/DepositAccountService";

interface DepositAccountState {
  modalStatus: boolean;
  modalLoading: boolean;
  fetchLoading: boolean;
  accounts: DepositAccount[] | null;
  formOptions: DepositAccountFormOptions | null;
  // Contas das empresas do cliente, agrupadas por empresa (home do cliente).
  clientGroups: ClientDepositGroup[] | null;
}

const initialState: DepositAccountState = {
  modalStatus: false,
  modalLoading: false,
  fetchLoading: false,
  accounts: null,
  formOptions: null,
  clientGroups: null,
};

const depositAccountSlice = createSlice({
  name: "depositAccount",
  initialState,
  reducers: {
    setDepositAccountModalStatus: (state, action: PayloadAction<boolean>) => {
      state.modalStatus = action.payload;
    },
    setDepositAccountModalLoading: (state, action: PayloadAction<boolean>) => {
      state.modalLoading = action.payload;
    },
    setDepositAccountFetchLoading: (state, action: PayloadAction<boolean>) => {
      state.fetchLoading = action.payload;
    },
    setDepositAccounts: (state, action: PayloadAction<DepositAccount[]>) => {
      state.accounts = action.payload;
    },
    setDepositAccountFormOptions: (
      state,
      action: PayloadAction<DepositAccountFormOptions>,
    ) => {
      state.formOptions = action.payload;
    },
    setClientDepositGroups: (
      state,
      action: PayloadAction<ClientDepositGroup[]>,
    ) => {
      state.clientGroups = action.payload;
    },
  },
});

export const {
  setDepositAccountModalStatus,
  setDepositAccountModalLoading,
  setDepositAccountFetchLoading,
  setDepositAccounts,
  setDepositAccountFormOptions,
  setClientDepositGroups,
} = depositAccountSlice.actions;

export default depositAccountSlice.reducer;

export const fetchDepositAccounts = (): AppThunk<Promise<void>> => async (
  dispatch,
  getState,
) => {
  dispatch(setDepositAccountFetchLoading(true));
  try {
    const orgId = getOrgId(getState());
    const accounts = await DepositAccountService.getByOrganization(orgId);
    dispatch(setDepositAccounts(accounts ?? []));
  } catch (e) {
    console.error(e);
  } finally {
    dispatch(setDepositAccountFetchLoading(false));
  }
};

export const fetchDepositAccountFormOptions = (): AppThunk<Promise<void>> => async (
  dispatch,
) => {
  try {
    const options = await DepositAccountService.getFormOptions();
    dispatch(setDepositAccountFormOptions(options));
  } catch (e) {
    console.error(e);
  }
};

export const createDepositAccount = (
  payload: CreateDepositAccountDto,
): AppThunk<Promise<void>> => async (dispatch, getState) => {
  dispatch(setDepositAccountModalLoading(true));
  try {
    const orgId = getOrgId(getState());
    const created = await DepositAccountService.create(orgId, payload);
    if (!created) throw new Error("erro ao adicionar a conta");
    dispatch(fetchDepositAccounts());
    dispatch(
      openToast({
        title: "Conta adicionada",
        message: "Conta de deposito cadastrada com sucesso.",
        effect: "success",
      }),
    );
    dispatch(setDepositAccountModalStatus(false));
  } catch (e) {
    console.error(e);
    dispatch(
      openToast({
        title: "Erro",
        message: "Nao foi possivel adicionar a conta.",
        effect: "error",
      }),
    );
  } finally {
    dispatch(setDepositAccountModalLoading(false));
  }
};

// Carrega as contas de deposito de TODAS as empresas a que o cliente esta
// vinculado e agrupa por empresa (uma request por empresa — clientes costumam
// ter uma so). Reusa o endpoint por organizacao. Define [] quando nao ha
// empresa/contas, para a home mostrar "sem nenhuma conta disponivel".
export const fetchClientDepositAccounts = (): AppThunk<Promise<void>> => async (
  dispatch,
  getState,
) => {
  const orgUsers: any[] = getState().user.userInfo?.organizationUsers ?? [];
  try {
    const groups = await Promise.all(
      orgUsers
        .map((ou) => ou?.organization)
        .filter((org) => org && org.id)
        .map(async (org: any) => {
          const accounts = await DepositAccountService.getByOrganization(org.id);
          return {
            organizationId: org.id,
            organizationName: org.organizationName,
            accounts: accounts ?? [],
          };
        }),
    );
    dispatch(setClientDepositGroups(groups));
  } catch (e) {
    console.error(e);
    dispatch(setClientDepositGroups([]));
  }
};
